import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Popover, PopoverModule } from 'primeng/popover';
import { CloseOverlayOnNavigationDirective } from '@tourfold/libraries/shared-ui';

/**
 * Frontend allow-list of reaction emojis. Mirrors the server default
 * (`comments.reactions.allowed` in backend `application.yml`). The server is the source of truth
 * — anything not on the server allow-list will be rejected with 422 `comments.reaction-emoji-not-allowed`
 * regardless of what the picker shows. Both sides must move together when the list changes.
 */
export const REACTION_ALLOW_LIST: readonly string[] = [
  '👍',
  '❤️',
  '😂',
  '🎉',
  '😮',
  '😢',
  '👀',
  '🙌',
] as const;

/**
 * Floating emoji picker anchored to the "+" button of {@link ReactionsBarComponent}. Renders the
 * eight {@link REACTION_ALLOW_LIST} entries in a horizontal row; click selects, keyboard nav
 * (left/right/home/end) moves the focus, enter selects, escape closes.
 *
 * The popover lifecycle (open/close) is owned by the parent — call {@link toggle} with the click
 * event. The picker emits {@link emojiSelected} when the user picks; the parent dispatches the
 * add/remove based on the comment's current state.
 */
@Component({
  selector: 'tf-reaction-picker',
  standalone: true,
  imports: [TranslateModule, PopoverModule, CloseOverlayOnNavigationDirective],
  templateUrl: './reaction-picker.component.html',
  styleUrl: './reaction-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReactionPickerComponent {
  /** Emitted when the user picks an emoji. The parent decides add vs remove. */
  readonly emojiSelected = output<string>();

  private readonly popover = viewChild.required<Popover>('op');
  private readonly listEl = viewChild<ElementRef<HTMLElement>>('listEl');

  protected readonly emojis = REACTION_ALLOW_LIST;

  /** Index of the keyboard-focused emoji while the popover is open. */
  protected readonly focusedIndex = signal(0);

  /** Toggle the popover anchored to the source button. Called by the parent. */
  toggle(event: Event): void {
    this.focusedIndex.set(0);
    this.popover().toggle(event);
  }

  /** Force-close the popover (used after a successful pick). */
  hide(): void {
    this.popover().hide();
  }

  protected onShow(): void {
    // Defer until after the popover has mounted; otherwise the buttons aren't in the DOM yet.
    queueMicrotask(() => this.focusEmojiAt(0));
  }

  protected onSelect(emoji: string): void {
    this.emojiSelected.emit(emoji);
    this.hide();
  }

  protected onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        this.moveFocus(1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.moveFocus(-1);
        break;
      case 'Home':
        event.preventDefault();
        this.focusEmojiAt(0);
        break;
      case 'End':
        event.preventDefault();
        this.focusEmojiAt(this.emojis.length - 1);
        break;
      case 'Escape':
        event.preventDefault();
        this.hide();
        break;
    }
  }

  private moveFocus(delta: number): void {
    const next = (this.focusedIndex() + delta + this.emojis.length) % this.emojis.length;
    this.focusEmojiAt(next);
  }

  private focusEmojiAt(index: number): void {
    this.focusedIndex.set(index);
    const buttons = this.listEl()?.nativeElement.querySelectorAll<HTMLButtonElement>(
      'button.tf-reaction-picker__emoji',
    );
    buttons?.item(index)?.focus();
  }
}
